import { Component, OnInit, OnDestroy, Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map } from 'rxjs/operators';
import { SitesService } from '~services/sites.service';
import { LmoDriverService } from '~services/lmo-driver.service';
import { trackById } from '~utilities/trackById';
import { MatSnackBar } from '@angular/material/snack-bar';

@Component({
  selector: 'sa-add-site-restriction-dialog',
  templateUrl: './add-site-restriction-dialog.component.html',
  styleUrls: ['./add-site-restriction-dialog.component.scss'],
})
export class AddSiteRestrictionDialogComponent implements OnInit, OnDestroy {
  public networkActive$$ = new BehaviorSubject<boolean>(false);
  siteSearchControl = new FormControl();
  selectAllText = 'Select All';
  driverName = this.data.driver.name;
  driverID = this.data.driver.id;

  private siteControlMap: { [key: string]: FormControl } = {};

  restrictionForm: FormGroup;
  private subs: Subscription[] = [];
  sites: any[] = [];
  filteredSites: any[] = [];

  constructor(
    private siteService: SitesService,
    private lmoDriverService: LmoDriverService,
    private dialogRef: MatDialogRef<AddSiteRestrictionDialogComponent>,
    private formBuilder: FormBuilder,
    private snackBar: MatSnackBar,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {}

  protected readonly trackById = trackById;

  ngOnInit(): void {
    this.restrictionForm = this.formBuilder.group({});

    this.subs.push(
      this.siteService.sites$.subscribe((sites) => {
        this.sites = sites;
        this.initializeFormControls();
      }),
      this.siteSearchControl.valueChanges
        .pipe(
          debounceTime(300),
          distinctUntilChanged(),
          map((search) => search.trim().toLowerCase()),
        )
        .subscribe((search: string) => {
          this.filteredSites = this.filterSites(search);
        }),
    );

    this.siteService.fetchActiveSites();
  }

  initializeFormControls(): void {
    // Clear existing form controls and site control map
    Object.keys(this.siteControlMap).forEach((key) => {
      this.restrictionForm.removeControl(key);
    });
    this.siteControlMap = {};

    this.filteredSites = [];

    if (this.data.driver && this.data.driver.siteRestrictions) {
      const restrictedSiteIds = this.data.driver.siteRestrictions.map((restriction) => restriction.siteId);

      this.filteredSites = this.sites
        .filter((site) => !restrictedSiteIds.includes(site.id))
        .sort((a, b) => a.name.localeCompare(b.name));

      // Add form controls for each filtered site
      this.filteredSites.forEach((site) => {
        const siteControl = this.formBuilder.control(false);
        const reasonControl = this.formBuilder.control('');
        this.restrictionForm.addControl(`site-${site.id}`, siteControl);
        this.restrictionForm.addControl(`reason-${site.id}`, reasonControl);
        this.siteControlMap[`site-${site.id}`] = siteControl;
      });
    } else {
      this.sites.forEach((site) => {
        const siteControl = this.formBuilder.control(false);
        const reasonControl = this.formBuilder.control('');
        this.restrictionForm.addControl(`site-${site.id}`, siteControl);
        this.restrictionForm.addControl(`reason-${site.id}`, reasonControl);
        this.siteControlMap[`site-${site.id}`] = siteControl;
        this.filteredSites.push(site);
      });
    }

    this.updateSelectAllText();
  }

  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }

  filterSites(search: string): any[] {
    if (!search) {
      this.filteredSites = [...this.sites];
    } else {
      const searchStr = search
        .toString()
        .trim()
        .toLowerCase();

      this.filteredSites = this.sites.filter((site) => {
        const siteName = site.name ? site.name.toString().toLowerCase() : '';
        return siteName.includes(searchStr);
      });
    }

    // Ensure form controls for all filtered sites exist
    this.filteredSites.forEach((site) => {
      const siteControlName = `site-${site.id}`;
      if (!this.siteControlMap[siteControlName]) {
        const siteControl = this.formBuilder.control(false);
        const reasonControl = this.formBuilder.control('');
        this.restrictionForm.addControl(siteControlName, siteControl);
        this.restrictionForm.addControl(`reason-${site.id}`, reasonControl);
        this.siteControlMap[siteControlName] = siteControl;
      }
    });

    // Remove controls for sites no longer in the filtered list
    Object.keys(this.siteControlMap).forEach((key) => {
      if (!this.filteredSites.some((site) => `site-${site.id}` === key)) {
        this.restrictionForm.removeControl(key);
        this.restrictionForm.removeControl(`reason-${key.split('-')[1]}`);
        delete this.siteControlMap[key];
      }
    });

    this.updateSelectAllText();

    return this.filteredSites;
  }

  onCancelClick(): void {
    this.dialogRef.close();
  }

  async onAddRestriction() {
    if (this.restrictionForm.valid && !this.networkActive$$.value) {
      try {
        this.networkActive$$.next(true);
        const selectedSitesWithReasons = this.getSelectedSitesWithReasons();
        let success = false;
        if (selectedSitesWithReasons.length > 0) {
          success = await this.lmoDriverService.banDriverFromSite(this.driverID, selectedSitesWithReasons);
        }
        if (success) {
          this.lmoDriverService.getLmoDrivers();
          this.dialogRef.close();
        } else {
          this.snackBar.open('Failed to add restriction!', null, {
            duration: 5000,
          });
        }
      } finally {
        this.networkActive$$.next(false);
      }
    }
  }

  getSelectedSitesWithReasons(): any[] {
    return this.filteredSites.reduce((acc, site) => {
      const siteSelected = this.restrictionForm.get(`site-${site.id}`).value;
      const formControlReason = this.restrictionForm.get(`reason-${site.id}`);
      const reason = formControlReason ? formControlReason.value : null;

      if (siteSelected) {
        acc.push({ siteId: site.id, siteName: site.name, reason: reason });
      }
      return acc;
    }, []);
  }

  toggleSelectAll(): void {
    const allChecked = this.areAllChecked();
    this.filteredSites.forEach((site) => {
      const siteControl = this.restrictionForm.get(`site-${site.id}`) as FormControl;
      siteControl.setValue(!allChecked);
    });
    this.updateSelectAllText();
  }

  areAllChecked(): boolean {
    return this.filteredSites.every((site) => {
      const siteControl = this.restrictionForm.get(`site-${site.id}`) as FormControl;
      return siteControl.value === true;
    });
  }

  updateSelectAllText(): void {
    this.selectAllText = this.areAllChecked() ? 'Unselect All' : 'Select All';
  }
}
